import { Link } from 'react-router-dom';

export interface ServiceCard {
  icon: string;
  title: string;
  bullets: string[];
  link: string;
}

export interface ServicesContent {
  eyebrow: string;
  headline: string;
  subheadline?: string;
  services: ServiceCard[];
  ctaText?: string;
  ctaLink?: string;
}

interface Props {
  content: ServicesContent;
}

const serviceImages = [
  'https://public.readdy.ai/ai/img_res/72db2c910e0a71b8ebcc679d1f661041.jpg',
  'https://readdy.ai/api/search-image?query=licensed%20plumber%20repairing%20underground%20sewer%20pipe%20outdoor%20excavation%20site%20daylight%20crew%20working%20hard%20hats%20safety%20vests%20authentic%20service%20photography%20golden%20hour&width=620&height=460&seq=svc-sewer-repair-002&orientation=landscape',
  'https://readdy.ai/api/search-image?query=professional%20plumber%20performing%20pipe%20repairs%20copper%20fittings%20close%20up%20hands%20tools%20indoor%20lighting%20authentic%20workmanship%20photography%20natural%20warm%20tones&width=620&height=460&seq=svc-plumbing-repairs-003&orientation=landscape',
  'https://readdy.ai/api/search-image?query=hydro%20jetting%20service%20technician%20yellow%20high%20pressure%20hose%20close-up%20action%20shot%20water%20spray%20industrial%20pipe%20cleaning%20real%20work%20photography%20authentic&width=620&height=460&seq=svc-hydro-jetting-004&orientation=landscape',
  'https://readdy.ai/api/search-image?query=plumber%20installing%20sewer%20pipe%20liner%20trenchless%20rehabilitation%20underground%20pipe%20interior%20professional%20licensed%20crew%20authentic%20service%20photography%20dramatic%20light&width=620&height=460&seq=svc-sewer-liners-005&orientation=landscape',
  'https://readdy.ai/api/search-image?query=plumber%20using%20mechanical%20rooter%20auger%20tool%20drain%20clearing%20professional%20service%20authentic%20close%20up%20action%20photography%20warm%20indoor%20light%20real%20technician%20working&width=620&height=460&seq=svc-rooter-service-006&orientation=landscape',
  'https://readdy.ai/api/search-image?query=professional%20plumber%20installing%20modern%20white%20tankless%20water%20heater%20unit%20wall%20mounted%20residential%20home%20interior%20close%20up%20tools%20hands%20warm%20light%20authentic%20installation%20photography&width=620&height=460&seq=svc-tankless-heater-007&orientation=landscape',
  'https://readdy.ai/api/search-image?query=commercial%20plumber%20working%20large%20diameter%20industrial%20pipe%20system%20modern%20commercial%20kitchen%20restaurant%20professional%20service%20licensed%20technician%20authentic%20photography%20warm%20interior%20light&width=620&height=460&seq=svc-commercial-plumbing-008&orientation=landscape',
  'https://readdy.ai/api/search-image?query=licensed%20plumber%20inspecting%20conventional%20hot%20water%20heater%20residential%20home%20interior%20close%20up%20tools%20pressure%20relief%20valve%20authentic%20service%20photography%20warm%20light&width=620&height=460&seq=svc-water-heater-service-009&orientation=landscape',
];

const ServicesOverview = ({ content }: Props) => {
  const { eyebrow, headline, subheadline, services, ctaText, ctaLink } = content;

  return (
    <section className="py-36 relative overflow-hidden bg-surface-soft">

      <div className="relative z-10 max-w-[1280px] mx-auto px-8">

        {/* ── HEADER ─────────────────────────────────────────── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-20">
          <div>
            <div className="inline-flex items-center gap-4 mb-7">
              <div className="h-px w-12 bg-gold" />
              <span className="text-gold text-[10px] tracking-[0.36em] uppercase font-sans font-semibold">
                {eyebrow}
              </span>
            </div>
            <h2
              className="font-serif text-charcoal font-bold leading-tight"
              style={{ fontSize: 'clamp(2.4rem, 4.2vw, 3.6rem)' }}
            >
              {headline}
            </h2>
            <div className="flex items-center gap-2.5 mt-6">
              <div className="h-[3px] w-16 bg-teal rounded-full" />
              <div className="h-[3px] w-5 rounded-full bg-teal/25" />
            </div>
          </div>
          {subheadline && (
            <p className="text-charcoal/45 text-[14px] max-w-[280px] font-sans leading-relaxed lg:text-right">{subheadline}</p>
          )}
        </div>

        {/* ── SERVICE GRID ───────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {services.map((service, idx) => {
            const imgSrc = serviceImages[idx] || serviceImages[0];
            return (
              <Link
                key={idx}
                to={service.link}
                className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-400 hover:-translate-y-2 flex flex-col"
                style={{
                  boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                  minHeight: '420px',
                }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 12px 48px rgba(0,0,0,0.12)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.06)')}
              >
                {/* Image top */}
                <div className="relative overflow-hidden flex-shrink-0" style={{ height: '200px' }}>
                  <img
                    src={imgSrc}
                    alt={service.title}
                    className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.07]"
                    style={{ filter: 'brightness(0.88) contrast(1.05) saturate(0.9)' }}
                  />
                  {/* Subtle gradient bottom on image */}
                  <div className="absolute bottom-0 left-0 right-0 h-14 pointer-events-none"
                    style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.15))' }} />

                  {/* Service number badge */}
                  <div className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 flex-shrink-0">
                    <span className="font-serif text-teal font-bold text-[11px]">{String(idx + 1).padStart(2, '0')}</span>
                  </div>

                  {/* Teal top sweep on hover */}
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-teal origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
                </div>

                {/* Content bottom */}
                <div className="relative p-7 flex flex-col flex-1">
                  {/* Icon */}
                  <div className="w-10 h-10 flex items-center justify-center mb-5 rounded-xl flex-shrink-0 transition-all duration-300"
                    style={{ background: 'rgba(26,74,82,0.08)', border: '1px solid rgba(26,74,82,0.14)' }}>
                    <i className={`${service.icon} text-teal text-lg`} />
                  </div>

                  <h3 className="font-serif text-charcoal font-bold text-[1.15rem] mb-4 leading-snug group-hover:text-teal transition-colors duration-300">
                    {service.title}
                  </h3>

                  <ul className="space-y-2 mb-auto">
                    {service.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-charcoal/55 text-[13px] font-sans leading-snug">
                        <i className="ri-checkbox-circle-line text-teal/60 text-sm mt-0.5 flex-shrink-0 group-hover:text-teal/90 transition-colors" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-7 flex items-center gap-2 text-teal/60 text-[11px] font-semibold tracking-[0.16em] uppercase group-hover:text-teal transition-all duration-300 group-hover:gap-3 pt-5 border-t border-surface-border">
                    Learn More <i className="ri-arrow-right-line text-sm" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* ── SERVICE TICKER ─────────────────────────────────── */}
        <div className="relative overflow-hidden border-y border-surface-border py-4 mb-14" style={{ marginLeft: '-2rem', marginRight: '-2rem', paddingLeft: '2rem', paddingRight: '2rem' }}>
          <div className="flex gap-12 animate-marquee whitespace-nowrap select-none">
            {['Drain Cleaning', 'Sewer Line Repair', 'Hydro Jetting', 'Rooter Service', 'CIPP Liners', 'Emergency Plumbing', 'Plumbing Repairs', 'Tankless Water Heaters',
              'Drain Cleaning', 'Sewer Line Repair', 'Hydro Jetting', 'Rooter Service', 'CIPP Liners', 'Emergency Plumbing', 'Plumbing Repairs', 'Tankless Water Heaters'].map((s, i) => (
              <span key={i} className="flex items-center gap-4 text-charcoal/25 text-[10.5px] font-sans tracking-[0.25em] uppercase">
                {s}<span className="w-1 h-1 bg-teal/30 rounded-full flex-shrink-0" />
              </span>
            ))}
          </div>
        </div>

        {ctaText && ctaLink && (
          <div className="text-center">
            <Link
              to={ctaLink}
              className="inline-flex items-center gap-3 bg-teal hover:bg-teal-mid text-white font-semibold text-[11px] px-14 py-[15px] rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.16em] uppercase group"
            >
              {ctaText}
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesOverview;
